import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, getDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import BookingCalendar from './BookingCalendar';

interface Board {
  id: string;
  venueId: string;
  boardNumber: number;
  name: string;
  isActive: boolean;
  openingHours: {
    [key: string]: { open: string; close: string; closed: boolean };
  };
  createdAt: any;
}

interface OpeningHours {
  open: string;
  close: string;
  closed: boolean;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_NAMES: { [key: string]: string } = {
  monday: 'Hétfő',
  tuesday: 'Kedd',
  wednesday: 'Szerda',
  thursday: 'Csütörtök',
  friday: 'Péntek',
  saturday: 'Szombat',
  sunday: 'Vasárnap'
};

const BoardManagement: React.FC = () => {
  const { user, isVenueOperator } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'boards' | 'bookings'>('bookings');
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [venueId, setVenueId] = useState<string | null>(null);
  const [venueName, setVenueName] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedBoards, setSelectedBoards] = useState<string[]>([]);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkHours, setBulkHours] = useState<{ [key: string]: OpeningHours }>({});
  const [newBoard, setNewBoard] = useState({
    boardNumber: 1,
    name: ''
  });

  useEffect(() => {
    loadVenueId();
  }, [user]);

  useEffect(() => {
    if (venueId) {
      loadBoards();
      loadVenueName();
    }
  }, [venueId]);

  const loadVenueId = async () => {
    if (!user || !isVenueOperator) return;

    try {
      // Check if there's a selected venue in localStorage (for operators with multiple venues)
      const selectedVenueId = localStorage.getItem('selectedVenueId');
      if (selectedVenueId) {
        setVenueId(selectedVenueId);
        return;
      }

      // Otherwise, use the user's default venueId
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData?.venueId) {
          setVenueId(userData.venueId);
        }
      }
    } catch (error) {
      console.error('Error loading venue ID:', error);
    }
  };

  const loadVenueName = async () => {
    if (!venueId) return;

    try {
      const venueDocRef = doc(db, 'venues', venueId);
      const venueDocSnap = await getDoc(venueDocRef);
      if (venueDocSnap.exists()) {
        const venueData = venueDocSnap.data();
        setVenueName(venueData?.name || 'Ismeretlen helyszín');
      }
    } catch (error) {
      console.error('Error loading venue name:', error);
    }
  };

  const loadBoards = async () => {
    if (!venueId) return;

    setLoading(true);
    try {
      const q = query(
        collection(db, 'boards'),
        where('venueId', '==', venueId),
        where('isActive', '==', true)
      );
      const boardsSnapshot = await getDocs(q);
      const boardsData: Board[] = [];

      boardsSnapshot.forEach((doc) => {
        boardsData.push({
          id: doc.id,
          ...doc.data()
        } as Board);
      });

      boardsData.sort((a, b) => a.boardNumber - b.boardNumber);
      setBoards(boardsData);
    } catch (error) {
      console.error('Error loading boards:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBoard = async () => {
    if (!venueId) return;

    try {
      const defaultHours: { [key: string]: OpeningHours } = {};
      DAYS.forEach(day => {
        defaultHours[day] = { open: '09:00', close: '22:00', closed: false };
      });

      await addDoc(collection(db, 'boards'), {
        venueId,
        boardNumber: newBoard.boardNumber,
        name: newBoard.name || `Tábla ${newBoard.boardNumber}`,
        isActive: true,
        openingHours: defaultHours,
        createdAt: serverTimestamp()
      });

      setShowAddModal(false);
      setNewBoard({ boardNumber: 1, name: '' });
      await loadBoards();
    } catch (error) {
      console.error('Error adding board:', error);
    }
  };

  const toggleBoardActive = async (boardId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'boards', boardId), {
        isActive: !currentStatus
      });
      await loadBoards();
    } catch (error) {
      console.error('Error toggling board status:', error);
    }
  };

  const updateBoardHours = async (boardId: string, hours: { [key: string]: OpeningHours }) => {
    try {
      await updateDoc(doc(db, 'boards', boardId), {
        openingHours: hours
      });
      await loadBoards();
    } catch (error) {
      console.error('Error updating board hours:', error);
    }
  };

  const toggleBoardSelection = (boardId: string) => {
    setSelectedBoards(prev =>
      prev.includes(boardId)
        ? prev.filter(id => id !== boardId)
        : [...prev, boardId]
    );
  };

  const selectAllBoards = () => {
    if (selectedBoards.length === boards.length) {
      setSelectedBoards([]);
    } else {
      setSelectedBoards(boards.map(b => b.id));
    }
  };

  const startBulkEdit = () => {
    if (selectedBoards.length === 0) return;

    const defaultHours: { [key: string]: OpeningHours } = {};
    DAYS.forEach(day => {
      defaultHours[day] = { open: '09:00', close: '22:00', closed: false };
    });
    setBulkHours(defaultHours);
    setBulkEditMode(true);
  };

  const applyBulkEdit = async () => {
    try {
      const updatePromises = selectedBoards.map(boardId =>
        updateDoc(doc(db, 'boards', boardId), {
          openingHours: bulkHours
        })
      );
      await Promise.all(updatePromises);
      setBulkEditMode(false);
      setSelectedBoards([]);
      await loadBoards();
    } catch (error) {
      console.error('Error applying bulk edit:', error);
    }
  };

  const bulkActivate = async () => {
    try {
      const updatePromises = selectedBoards.map(boardId =>
        updateDoc(doc(db, 'boards', boardId), {
          isActive: true
        })
      );
      await Promise.all(updatePromises);
      setSelectedBoards([]);
      await loadBoards();
    } catch (error) {
      console.error('Error bulk activating boards:', error);
    }
  };

  const bulkDeactivate = async () => {
    try {
      const updatePromises = selectedBoards.map(boardId =>
        updateDoc(doc(db, 'boards', boardId), {
          isActive: false
        })
      );
      await Promise.all(updatePromises);
      setSelectedBoards([]);
      await loadBoards();
    } catch (error) {
      console.error('Error bulk deactivating boards:', error);
    }
  };

  const deleteBoard = async (boardId: string, boardName: string) => {
    if (!window.confirm(`Biztosan törli a "${boardName}" táblát? Ez a művelet nem vonható vissza!`)) {
      return;
    }

    try {
      // Delete all bookings associated with this board
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('boardId', '==', boardId)
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);

      const deleteBookingPromises = bookingsSnapshot.docs.map(bookingDoc =>
        updateDoc(doc(db, 'bookings', bookingDoc.id), { isActive: false })
      );
      await Promise.all(deleteBookingPromises);

      // Soft delete the board (set isActive to false)
      await updateDoc(doc(db, 'boards', boardId), {
        isActive: false,
        deletedAt: serverTimestamp()
      });

      await loadBoards();
      alert('Tábla sikeresen törölve!');
    } catch (error) {
      console.error('Error deleting board:', error);
      alert('Hiba történt a törlés során.');
    }
  };

  if (!isVenueOperator) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card-dark p-8 text-center">
          <p className="text-[#B8B8C3]">Nincs jogosultsága az oldal megtekintéséhez.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">🎯 {t('venueOperation.boards.title')}</h1>
          <p className="text-[#B8B8C3]">{venueName}</p>
        </div>
        {activeTab === 'boards' && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
          >
            + {t('venueOperation.boards.addBoard')}
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="card-dark p-2 flex gap-2">
        <button
          onClick={() => setActiveTab('bookings')}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === 'bookings'
            ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_0_20px_rgba(6,182,212,0.5)]'
            : 'bg-[#31383D] text-[#B8B8C3] hover:bg-[#3d4758]'
            }`}
        >
          � {t('venueOperation.boards.bookingsTab')}
        </button>
        <button
          onClick={() => setActiveTab('boards')}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === 'boards'
            ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_0_20px_rgba(6,182,212,0.5)]'
            : 'bg-[#31383D] text-[#B8B8C3] hover:bg-[#3d4758]'
            }`}
        >
          � {t('venueOperation.boards.boardsTab')}
        </button>
      </div>

      {activeTab === 'bookings' ? (
        <BookingCalendar venueId={venueId || ''} boards={boards} />
      ) : (
        <>
          {boards.length > 0 && (
            <div className="card-dark p-4 flex items-center gap-4 flex-wrap">
              <button
                onClick={selectAllBoards}
                className="px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
              >
                {selectedBoards.length === boards.length ? 'Kijelölés megszüntetése' : 'Összes kijelölése'}
              </button>
              {selectedBoards.length > 0 && (
                <>
                  <button
                    onClick={startBulkEdit}
                    className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                  >
                    Nyitvatartás beállítása ({selectedBoards.length} tábla)
                  </button>
                  <button
                    onClick={bulkActivate}
                    className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(34,197,94,0.5)] transition-all"
                  >
                    Aktiválás ({selectedBoards.length})
                  </button>
                  <button
                    onClick={bulkDeactivate}
                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] transition-all"
                  >
                    Deaktiválás ({selectedBoards.length})
                  </button>
                </>
              )}
            </div>
          )}

          {boards.length === 0 ? (
            <div className="card-dark p-12 text-center">
              <div className="w-20 h-20 bg-[#15191E] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-[#B8B8C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Nincsenek táblák</h3>
              <p className="text-[#B8B8C3]">Adja hozzá az első táblát a helyszínhez.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {boards.map((board) => (
                <BoardCard
                  key={board.id}
                  board={board}
                  isSelected={selectedBoards.includes(board.id)}
                  onToggleSelection={() => toggleBoardSelection(board.id)}
                  onToggleActive={() => toggleBoardActive(board.id, board.isActive)}
                  onUpdateHours={(hours) => updateBoardHours(board.id, hours)}
                  onDelete={() => deleteBoard(board.id, board.name)}
                />
              ))}
            </div>
          )}

          {/* Add Board Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-[#15191E] rounded-lg p-6 max-w-md w-full mx-4 border border-[#31383D]">
                <h3 className="text-2xl font-bold text-white mb-4">Új tábla hozzáadása</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Tábla száma</label>
                    <input
                      type="number"
                      value={newBoard.boardNumber}
                      onChange={(e) => setNewBoard({ ...newBoard, boardNumber: parseInt(e.target.value) || 1 })}
                      min="1"
                      className="w-full px-4 py-3 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Név (opcionális)</label>
                    <input
                      type="text"
                      value={newBoard.name}
                      onChange={(e) => setNewBoard({ ...newBoard, name: e.target.value })}
                      placeholder={`Tábla ${newBoard.boardNumber}`}
                      className="w-full px-4 py-3 bg-[#15191E] border border-[#31383D] rounded-lg text-white placeholder-[#8a8f99] focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
                  >
                    Mégse
                  </button>
                  <button
                    onClick={addBoard}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
                  >
                    Hozzáadás
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Edit Modal */}
          {bulkEditMode && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
              <div className="bg-[#15191E] rounded-lg p-6 max-w-2xl w-full mx-4 my-8 border border-[#31383D]">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Nyitvatartás beállítása ({selectedBoards.length} tábla)
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {DAYS.map((day) => (
                    <div key={day} className="flex items-center gap-3 bg-[#1d232a] p-3 rounded-lg">
                      <div className="w-24">
                        <p className="text-white font-semibold">{DAY_NAMES[day]}</p>
                      </div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={bulkHours[day]?.closed || false}
                          onChange={(e) => setBulkHours({
                            ...bulkHours,
                            [day]: { ...bulkHours[day], closed: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-[#B8B8C3]">Zárva</span>
                      </label>
                      {!bulkHours[day]?.closed && (
                        <>
                          <input
                            type="time"
                            value={bulkHours[day]?.open || '09:00'}
                            onChange={(e) => setBulkHours({
                              ...bulkHours,
                              [day]: { ...bulkHours[day], open: e.target.value, closed: false }
                            })}
                            className="px-3 py-2 bg-[#15191E] border border-[#31383D] rounded text-white"
                          />
                          <span className="text-[#B8B8C3]">-</span>
                          <input
                            type="time"
                            value={bulkHours[day]?.close || '22:00'}
                            onChange={(e) => setBulkHours({
                              ...bulkHours,
                              [day]: { ...bulkHours[day], close: e.target.value, closed: false }
                            })}
                            className="px-3 py-2 bg-[#15191E] border border-[#31383D] rounded text-white"
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setBulkEditMode(false)}
                    className="flex-1 px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
                  >
                    Mégse
                  </button>
                  <button
                    onClick={applyBulkEdit}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(34,197,94,0.5)] transition-all"
                  >
                    Alkalmaz
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface BoardCardProps {
  board: Board;
  isSelected: boolean;
  onToggleSelection: () => void;
  onToggleActive: () => void;
  onUpdateHours: (hours: { [key: string]: OpeningHours }) => void;
  onDelete: () => void;
}

const BoardCard: React.FC<BoardCardProps> = ({ board, isSelected, onToggleSelection, onToggleActive, onUpdateHours, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [hours, setHours] = useState(board.openingHours || {});

  const saveHours = () => {
    onUpdateHours(hours);
    setEditMode(false);
  };

  return (
    <div className={`card-dark p-4 border-2 ${isSelected ? 'border-cyan-500' : 'border-transparent'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelection}
            className="w-5 h-5"
          />
          <div>
            <h3 className="text-xl font-bold text-white">{board.name}</h3>
            <p className="text-sm text-[#B8B8C3]">Tábla #{board.boardNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleActive}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${board.isActive
              ? 'bg-green-500/20 text-green-400 border border-green-500'
              : 'bg-red-500/20 text-red-400 border border-red-500'
              }`}
          >
            {board.isActive ? 'Aktív' : 'Inaktív'}
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
          >
            {isExpanded ? 'Bezár' : 'Nyitvatartás'}
          </button>
          <button
            onClick={onDelete}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Törlés
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-[#31383D]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold text-white">Nyitvatartási idők</h4>
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="px-3 py-1 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition-colors"
              >
                Szerkesztés
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setHours(board.openingHours);
                    setEditMode(false);
                  }}
                  className="px-3 py-1 bg-[#31383D] text-white rounded hover:bg-[#3d4758] transition-colors"
                >
                  Mégse
                </button>
                <button
                  onClick={saveHours}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                >
                  Mentés
                </button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {DAYS.map((day) => (
              <div key={day} className="flex items-center gap-3 bg-[#1d232a] p-3 rounded-lg">
                <div className="w-24">
                  <p className="text-white font-semibold">{DAY_NAMES[day]}</p>
                </div>
                {editMode ? (
                  <>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={hours[day]?.closed || false}
                        onChange={(e) => setHours({
                          ...hours,
                          [day]: { ...hours[day], closed: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-[#B8B8C3]">Zárva</span>
                    </label>
                    {!hours[day]?.closed && (
                      <>
                        <input
                          type="time"
                          value={hours[day]?.open || '09:00'}
                          onChange={(e) => setHours({
                            ...hours,
                            [day]: { ...hours[day], open: e.target.value, closed: false }
                          })}
                          className="px-3 py-2 bg-[#15191E] border border-[#31383D] rounded text-white"
                        />
                        <span className="text-[#B8B8C3]">-</span>
                        <input
                          type="time"
                          value={hours[day]?.close || '22:00'}
                          onChange={(e) => setHours({
                            ...hours,
                            [day]: { ...hours[day], close: e.target.value, closed: false }
                          })}
                          className="px-3 py-2 bg-[#15191E] border border-[#31383D] rounded text-white"
                        />
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-[#B8B8C3]">
                    {hours[day]?.closed ? (
                      <span className="text-red-400">Zárva</span>
                    ) : (
                      <span>{hours[day]?.open || '09:00'} - {hours[day]?.close || '22:00'}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardManagement;
